import {
  type Identifier,
  useGetList,
  useGetMany,
  useListContext,
} from "ra-core";

import { useMemo } from "react";

import type { Contact, Task as TData } from "../types";

import { Task } from "./Task";

import { isDone, isRecentlyDone } from "./tasksPredicate";

type TaskContactLink = {
  id: Identifier;

  task_id: Identifier;

  contact_id: Identifier;

  created_at?: string;
};

export const TasksIterator = ({
  showCompleted = false,
  showContact,
  showAssignee,
  className,
}: {
  showCompleted?: boolean;
  showContact?: boolean;
  showAssignee?: boolean;
  className?: string;
}) => {
  const { data = [], error, isPending } = useListContext<TData>();

  const tasks = useMemo(
    () =>
      data.filter(
        (task) => showCompleted || !isDone(task) || isRecentlyDone(task),
      ),
    [data, showCompleted],
  );

  /*
   * Только задачи, которые реально
   * находятся на текущей странице
   * конкретной группы.
   *
   * Поэтому "Загрузить ещё"
   * автоматически расширит и этот
   * общий запрос связей.
   */
  const taskIds = useMemo(() => tasks.map((task) => task.id), [tasks]);

  /*
   * Вместо одного запроса
   * task_contacts на КАЖДУЮ задачу
   * загружаем все связи видимых
   * задач сразу.
   */
  const { data: taskContactLinks = [] } = useGetList<TaskContactLink>(
    "task_contacts",
    {
      pagination: {
        page: 1,
        perPage: 1000,
      },

      sort: {
        field: "created_at",
        order: "ASC",
      },

      filter: {
        "task_id@in": `(${taskIds.join(",")})`,
      },
    },
    {
      enabled: !!showContact && taskIds.length > 0,
    },
  );

  /*
   * Собираем все нужные contact_id.
   *
   * tasks.contact_id оставляем
   * как fallback для старых задач,
   * которые ещё могут не иметь
   * записи в task_contacts.
   */
  const contactIds = useMemo(() => {
    const result: Identifier[] = [];

    const seen = new Set<string>();

    const addId = (id: Identifier | null | undefined) => {
      if (id == null) {
        return;
      }

      const key = String(id);

      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      result.push(id);
    };

    tasks.forEach((task) => {
      addId(task.contact_id);
    });

    taskContactLinks.forEach((relation) => {
      addId(relation.contact_id);
    });

    return result;
  }, [tasks, taskContactLinks]);

  /*
   * И один общий запрос contacts
   * для всей видимой группы.
   */
  const { data: linkedContacts = [] } = useGetMany<Contact>(
    "contacts",
    {
      ids: contactIds,
    },
    {
      enabled: !!showContact && contactIds.length > 0,
    },
  );

  /*
   * Быстрый справочник contact.id
   * -> сама запись контакта.
   */
  const contactsById = useMemo(() => {
    const map = new Map<string, Contact>();

    linkedContacts.forEach((contact) => {
      map.set(String(contact.id), contact);
    });

    return map;
  }, [linkedContacts]);

  /*
   * Готовим контакты уже по задачам:
   * task.id -> Contact[].
   *
   * Первый legacy contact_id
   * сохраняем первым, затем добавляем
   * связи из task_contacts без дублей.
   */
  const contactsByTaskId = useMemo(() => {
    const result = new Map<string, Contact[]>();

    tasks.forEach((task) => {
      const ids: Identifier[] = [];

      const seen = new Set<string>();

      const addId = (id: Identifier | null | undefined) => {
        if (id == null) {
          return;
        }

        const key = String(id);

        if (seen.has(key)) {
          return;
        }

        seen.add(key);
        ids.push(id);
      };

      addId(task.contact_id);

      taskContactLinks.forEach((relation) => {
        if (String(relation.task_id) !== String(task.id)) {
          return;
        }

        addId(relation.contact_id);
      });

      const contacts = ids
        .map((id) => contactsById.get(String(id)))
        .filter((contact): contact is Contact => !!contact);

      result.set(String(task.id), contacts);
    });

    return result;
  }, [tasks, taskContactLinks, contactsById]);

  if (isPending || error || tasks.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 md:space-y-2 ${className || ""}`}>
      {tasks.map((task) => (
        <Task
          task={task}
          showContact={showContact}
          showAssignee={showAssignee}
          linkedContacts={
            showContact ? (contactsByTaskId.get(String(task.id)) ?? []) : []
          }
          key={task.id}
        />
      ))}
    </div>
  );
};
